import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  Card,
  Chip,
  Tab,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Stack,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Info, Search, Refresh } from '@mui/icons-material';
import { TabPanel } from '@mui/lab';
import HeaderHelpPage from '../../../components/HeaderHelpPage';
import ExceptionHandlingDialog from '../../../components/finance-enhancement/ExceptionHandlingDialog';
import TabsMenu from '../../../components/TabsMenu';
import CircularLoader from '../../../components/circularLoader';
import { sendDataWithPost } from '../../../services/services';
import api from '../../../services/api';
import { showErrorMsg, showSuccessMsg } from '../../../utils/toast';
import moment from 'moment';
import fileDownload from 'js-file-download';
import ButtonCustom from '../../../components/ButtonCustom';
import { styles } from '../../../utils/styles';
import { EXCHANGE_RATE_CURRENCIES, PENDING_TYPES, getPendingTypeKey, INCOMING_PENDING_FILTER_START_DATE, EXCEPTION_ACTION_DEFAULT, EXCEPTION_SOURCE } from '../../../utils/constants';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { APP_USER_SESSION } from '../../../constant/AppConstants';
import * as StorageUtils from '../../../utils/storageUtils';

const DEFAULT_CURRENCY = 'BTN';
const CURRENCY_OPTIONS = [DEFAULT_CURRENCY, 'USD', 'SGD', ...EXCHANGE_RATE_CURRENCIES.filter((c) => c !== 'USD')];

const Pending = () => {
  const userRole = StorageUtils.getParsedValue(APP_USER_SESSION).role;
  const exceptionHandlingRole = userRole.exception_handling || [];

  const [selectedTab, setSelectedTab] = useState(PENDING_TYPES.NO_FUND.label);
  const [noFundData, setNoFundData] = useState([]);
  const [notSettledData, setNotSettledData] = useState([]);
  const [noPayoutData, setNoPayoutData] = useState([]);
  const [noFundCount, setNoFundCount] = useState(0);
  const [notSettledCount, setNotSettledCount] = useState(0);
  const [noPayoutCount, setNoPayoutCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [noFundPage, setNoFundPage] = useState(0);
  const [notSettledPage, setNotSettledPage] = useState(0);
  const [noPayoutPage, setNoPayoutPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [settlementCycle, setSettlementCycle] = useState('');
  const [settlementCycleOptions, setSettlementCycleOptions] = useState([]);
  const [startDate, setStartDate] = useState(moment().subtract(33, 'days'));
  const [endDate, setEndDate] = useState(moment().subtract(2, 'days'));
  const [transactionId, setTransactionId] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [terminalId, setTerminalId] = useState('');
  const lastLoadedParams = useRef({});
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
  const [exceptionRemarks, setExceptionRemarks] = useState('');
  const [exceptionAction, setExceptionAction] = useState('');
  const [exceptionPushLoading, setExceptionPushLoading] = useState({});
  const [selectedExceptionRow, setSelectedExceptionRow] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const paramsMatch = useCallback((a, b) => (
    (a?.currency || null) === (b?.currency || null) &&
    (a?.settlementCycle || null) === (b?.settlementCycle || null) &&
    (a?.startDate || null) === (b?.startDate || null) &&
    (a?.endDate || null) === (b?.endDate || null) &&
    (a?.transactionId || null) === (b?.transactionId || null) &&
    (a?.merchantId || null) === (b?.merchantId || null) &&
    (a?.merchantName || null) === (b?.merchantName || null) &&
    (a?.terminalId || null) === (b?.terminalId || null) &&
    a?.page === b?.page &&
    a?.pageSize === b?.pageSize
  ), []);

  const handleTabChange = (event, value) => {
    setSelectedTab(value);

    const pendingType = getPendingTypeKey(value);
    const currentPage = value === PENDING_TYPES.NO_FUND.label ? noFundPage 
      : value === PENDING_TYPES.NOT_SETTLED.label ? notSettledPage 
      : noPayoutPage;
    loadTabData(pendingType, currentPage + 1, rowsPerPage);
  };

  // Transform data to match UI format (backend already provides standardized format and sorted)
  const transformData = useCallback((data) => {
    return data.map((item, index) => ({
      id: item.id || index,
      transaction_id: item.transaction_id || '',
      transaction_time: item.transaction_time || '',
      transaction_type: item.transaction_type || '',
      transaction_state: item.transaction_state || '',
      terminal_id: item.terminal_id || '',
      merchant_id: item.merchant_id || '',
      merchant_name: item.merchant_name || '',
      transaction_amount: parseFloat(item.transaction_amount || 0),
      currency: item.currency || '',
      brand: item.brand || '',
      host: item.host || '',
      acquirer: item.acquirer || '',
      acquirer_transaction_id: item.acquirer_transaction_id || '',
      gateway_transaction_id: item.gateway_transaction_id || '',
      comparison_status: item.comparison_status || '',
      exception_source: item.exception_source || EXCEPTION_SOURCE.PENDING_NO_FUND,
      exception_action: item.exception_action || EXCEPTION_ACTION_DEFAULT,
    }));
  }, []);

  // Load data for a specific tab with pagination
  const loadTabData = useCallback(async (pendingType, page, pageSize) => {
    const startDateFormatted = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
    const endDateFormatted = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
    const currentParams = { 
      currency: currency || null, 
      settlementCycle: settlementCycle || null, 
      startDate: startDateFormatted || null,
      endDate: endDateFormatted || null, 
      transactionId: transactionId || null,
      merchantId: merchantId || null,
      merchantName: merchantName || null,
      terminalId: terminalId || null,
      page, 
      pageSize 
    };
    if (paramsMatch(lastLoadedParams.current[pendingType], currentParams)) {
      return;
    }
    setLoading(true);

    try {
      const queryParams = new URLSearchParams({ page, page_size: pageSize }).toString();
      const url = `/payout/pending/pending_list/?${queryParams}`;
      
      const response = await sendDataWithPost(url, {
        currency: currency || null,
        settlement_cycle: settlementCycle || null,
        pending_type: pendingType,
        start_date: startDateFormatted || null,
        end_date: endDateFormatted || null,
        transaction_id: transactionId || null,
        merchant_id: merchantId || null,
        merchant_name: merchantName || null,
        terminal_id: terminalId || null,
      });
      const data = response.data || {};
      const results = data.results || [];
      lastLoadedParams.current[pendingType] = currentParams;
      if (pendingType === PENDING_TYPES.NO_FUND.key) {
        setNoFundData(transformData(results));
      } else if (pendingType === PENDING_TYPES.NOT_SETTLED.key) {
        setNotSettledData(transformData(results));
      } else if (pendingType === PENDING_TYPES.NO_PAYOUT.key) {
        setNoPayoutData(transformData(results));
      }
    } catch (error) {
      console.error(`Failed to load ${pendingType} data:`, error);
      showErrorMsg(error.response?.data?.detail || `Failed to load ${pendingType} data`);
      
      if (pendingType === PENDING_TYPES.NO_FUND.key) {
        setNoFundData([]);
      } else if (pendingType === PENDING_TYPES.NOT_SETTLED.key) {
        setNotSettledData([]);
      } else if (pendingType === PENDING_TYPES.NO_PAYOUT.key) {
        setNoPayoutData([]);
      }
    } finally {
      setLoading(false);
    }
  }, [transformData, currency, settlementCycle, startDate, endDate, paramsMatch]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      
      const pendingType = getPendingTypeKey(PENDING_TYPES.NO_FUND.label);
      const queryParams = new URLSearchParams({ page: 1, page_size: 50 }).toString();
      const listUrl = `/payout/pending/pending_list/?${queryParams}`;
        const startDateFormatted = moment().subtract(33, 'days').format('YYYY-MM-DD');
        const endDateFormatted = moment().subtract(2, 'days').format('YYYY-MM-DD');
        const filterBody = { currency: DEFAULT_CURRENCY, settlement_cycle: null, start_date: startDateFormatted, end_date: endDateFormatted, transaction_id: null, merchant_id: null, merchant_name: null, terminal_id: null };
        const listBody = { currency: DEFAULT_CURRENCY, settlement_cycle: null, start_date: startDateFormatted, end_date: endDateFormatted, pending_type: pendingType, transaction_id: null, merchant_id: null, merchant_name: null, terminal_id: null };
        
        try {
          const [filterResponse, listResponse] = await Promise.all([
            sendDataWithPost('/payout/pending/filter_conditions/', filterBody),
            sendDataWithPost(listUrl, listBody)
          ]);
          const filterData = filterResponse.data || {};
          setSettlementCycleOptions(filterData.settlement_cycles || []);
          setNoFundCount(filterData.no_fund_count ?? 0);
          setNotSettledCount(filterData.not_settled_count ?? 0);
          setNoPayoutCount(filterData.no_payout_count ?? 0);
          const results = listResponse.data?.results || [];
          setNoFundData(transformData(results));
          setNotSettledData([]);
          setNoPayoutData([]);
          lastLoadedParams.current[PENDING_TYPES.NO_FUND.key] = { currency: DEFAULT_CURRENCY, settlementCycle: null, startDate: startDateFormatted, endDate: endDateFormatted, transactionId: null, merchantId: null, merchantName: null, terminalId: null, page: 1, pageSize: 50 };
      } catch (error) {
        console.error('Failed to load initial data:', error);
        showErrorMsg(error.response?.data?.detail || 'Failed to load data');
        setNoFundCount(0);
        setNotSettledCount(0);
        setNoPayoutCount(0);
        setNoFundData([]);
        setNotSettledData([]);
        setNoPayoutData([]);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);


  const handleChangePage = (event, newPage) => {
    const pendingType = getPendingTypeKey(selectedTab);
    
    if (selectedTab === PENDING_TYPES.NO_FUND.label) {
      setNoFundPage(newPage);
    } else if (selectedTab === PENDING_TYPES.NOT_SETTLED.label) {
      setNotSettledPage(newPage);
    } else {
      setNoPayoutPage(newPage);
    }
    
    loadTabData(pendingType, newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    
    setNoFundPage(0);
    setNotSettledPage(0);
    setNoPayoutPage(0);
    
    const pendingType = getPendingTypeKey(selectedTab);
    loadTabData(pendingType, 1, newRowsPerPage);
  };

  const handleSearch = async () => {
    setLoading(true);
    setNoFundPage(0);
    setNotSettledPage(0);
    setNoPayoutPage(0);
    const pendingType = getPendingTypeKey(selectedTab);
    const startDateFormatted = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
    const endDateFormatted = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
    const filterBody = { currency: currency || null, settlement_cycle: settlementCycle || null, start_date: startDateFormatted || null, end_date: endDateFormatted || null, transaction_id: transactionId || null, merchant_id: merchantId || null, merchant_name: merchantName || null, terminal_id: terminalId || null };
    const queryParams = new URLSearchParams({ page: 1, page_size: rowsPerPage }).toString();
    const listUrl = `/payout/pending/pending_list/?${queryParams}`;
    const listBody = { currency: currency || null, settlement_cycle: settlementCycle || null, start_date: startDateFormatted || null, end_date: endDateFormatted || null, pending_type: pendingType, transaction_id: transactionId || null, merchant_id: merchantId || null, merchant_name: merchantName || null, terminal_id: terminalId || null };
    
    try {
      const [filterResponse, listResponse] = await Promise.all([
        sendDataWithPost('/payout/pending/filter_conditions/', filterBody),
        sendDataWithPost(listUrl, listBody)
      ]);
      const filterData = filterResponse.data || {};
      setSettlementCycleOptions(filterData.settlement_cycles || []);
      setNoFundCount(filterData.no_fund_count ?? 0);
      setNotSettledCount(filterData.not_settled_count ?? 0);
      setNoPayoutCount(filterData.no_payout_count ?? 0);
      const results = listResponse.data?.results || [];
      lastLoadedParams.current[pendingType] = { currency: currency || null, settlementCycle: settlementCycle || null, startDate: startDateFormatted || null, endDate: endDateFormatted || null, transactionId: transactionId || null, merchantId: merchantId || null, merchantName: merchantName || null, terminalId: terminalId || null, page: 1, pageSize: rowsPerPage };
      if (pendingType === PENDING_TYPES.NO_FUND.key) {
        setNoFundData(transformData(results));
      } else if (pendingType === PENDING_TYPES.NOT_SETTLED.key) {
        setNotSettledData(transformData(results));
      } else {
        setNoPayoutData(transformData(results));
      }
    } catch (error) {
      console.error('Failed to load pending data:', error);
      showErrorMsg(error.response?.data?.detail || 'Failed to load pending data');
      setNoFundCount(0);
      setNotSettledCount(0);
      setNoPayoutCount(0);
      setNoFundData([]);
      setNotSettledData([]);
      setNoPayoutData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCurrency(DEFAULT_CURRENCY);
    setSettlementCycle('');
    setStartDate(moment().subtract(33, 'days'));
    setEndDate(moment().subtract(2, 'days'));
    setTransactionId('');
    setMerchantId('');
    setMerchantName('');
    setTerminalId('');
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const startDateFormatted = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
      const endDateFormatted = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
      const body = {
        currency: currency || null,
        settlement_cycle: settlementCycle || null,
        start_date: startDateFormatted || null,
        end_date: endDateFormatted || null,
        transaction_id: transactionId || null,
        merchant_id: merchantId || null,
        merchant_name: merchantName || null,
        terminal_id: terminalId || null,
      };

      const response = await api.post('/payout/pending/export_excel/', body, {
        responseType: 'blob',
      });

      let filename = `pending_${moment().utcOffset(8).format('YYYYMMDD_HHmmss')}.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
        const unquotedMatch = contentDisposition.match(/filename=([^;\s]+)/i);
        if (quotedMatch && quotedMatch[1]) {
          filename = quotedMatch[1].trim();
        } else if (unquotedMatch && unquotedMatch[1]) {
          filename = unquotedMatch[1].trim();
        }
      }

      fileDownload(response.data, filename);
    } catch (error) {
      console.error('Failed to export pending data:', error);
      showErrorMsg(error.response?.data?.detail || 'Failed to export pending data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenExceptionDialog = (row) => {
    setSelectedExceptionRow(row);
    setExceptionRemarks('');
    setExceptionAction(EXCEPTION_ACTION_DEFAULT);
    setExceptionDialogOpen(true);
  };

  const handleCloseExceptionDialog = () => {
    setExceptionDialogOpen(false);
    setSelectedExceptionRow(null);
    setExceptionRemarks('');
    setExceptionAction(EXCEPTION_ACTION_DEFAULT);
  };

  const refreshCurrentTabData = useCallback(async () => {
    const pendingType = getPendingTypeKey(selectedTab);
    const currentPage = selectedTab === PENDING_TYPES.NO_FUND.label ? noFundPage : selectedTab === PENDING_TYPES.NOT_SETTLED.label ? notSettledPage : noPayoutPage;
    const startDateFormatted = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
    const endDateFormatted = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
    const filterBody = { currency: currency || null, settlement_cycle: settlementCycle || null, start_date: startDateFormatted || null, end_date: endDateFormatted || null, transaction_id: transactionId || null, merchant_id: merchantId || null, merchant_name: merchantName || null, terminal_id: terminalId || null };
    const queryParams = new URLSearchParams({ page: currentPage + 1, page_size: rowsPerPage }).toString();
    const listUrl = `/payout/pending/pending_list/?${queryParams}`;
    const listBody = { currency: currency || null, settlement_cycle: settlementCycle || null, start_date: startDateFormatted || null, end_date: endDateFormatted || null, pending_type: pendingType, transaction_id: transactionId || null, merchant_id: merchantId || null, merchant_name: merchantName || null, terminal_id: terminalId || null };

    lastLoadedParams.current[pendingType] = undefined;
    setLoading(true);
    try {
      const [filterResponse, listResponse] = await Promise.all([
        sendDataWithPost('/payout/pending/filter_conditions/', filterBody),
        sendDataWithPost(listUrl, listBody)
      ]);
      const filterData = filterResponse.data || {};
      setSettlementCycleOptions(filterData.settlement_cycles || []);
      setNoFundCount(filterData.no_fund_count ?? 0);
      setNotSettledCount(filterData.not_settled_count ?? 0);
      setNoPayoutCount(filterData.no_payout_count ?? 0);
      const results = listResponse.data?.results || [];
      lastLoadedParams.current[pendingType] = { currency: currency || null, settlementCycle: settlementCycle || null, startDate: startDateFormatted || null, endDate: endDateFormatted || null, transactionId: transactionId || null, merchantId: merchantId || null, merchantName: merchantName || null, terminalId: terminalId || null, page: currentPage + 1, pageSize: rowsPerPage };
      if (pendingType === PENDING_TYPES.NO_FUND.key) {
        setNoFundData(transformData(results));
      } else if (pendingType === PENDING_TYPES.NOT_SETTLED.key) {
        setNotSettledData(transformData(results));
      } else {
        setNoPayoutData(transformData(results));
      }
    } catch (error) {
      console.error('Failed to refresh pending data:', error);
      showErrorMsg(error.response?.data?.detail || 'Failed to refresh pending data');
    } finally {
      setLoading(false);
    }
  }, [selectedTab, noFundPage, notSettledPage, noPayoutPage, rowsPerPage, currency, settlementCycle, startDate, endDate, transformData]);

  const handleConfirmException = async () => {
    if (!selectedExceptionRow) return;

    const newAction = (exceptionAction || selectedExceptionRow.exception_action || '').trim();
    const newRemarks = (exceptionRemarks || '').trim();
    if (!newAction) {
      showErrorMsg('Action is required');
      return;
    }
    if (!newRemarks) {
      showErrorMsg('Remarks is required');
      return;
    }

    const recordId = selectedExceptionRow.id;
    try {
      setExceptionPushLoading(prev => ({ ...prev, [recordId]: true }));
      const body = {
        acquirer_transaction_id: null,
        gateway_transaction_id: selectedExceptionRow.transaction_id,
        transaction_datetime: selectedExceptionRow.transaction_time,
        acquirer: selectedExceptionRow.acquirer,
        source: EXCEPTION_SOURCE.PENDING_NO_FUND,
        action: newAction,
        remarks: newRemarks,
      };
      const response = await sendDataWithPost('/payout/ingestor/push_to_exception/', body);
      if (response.status !== 200 && response.status !== 201) {
        return;
      }
      showSuccessMsg('Pushed to exception handling successfully');
      handleCloseExceptionDialog();
      await refreshCurrentTabData();
    } catch (error) {
      console.error('Failed to push to exception handling:', error);
      showErrorMsg('Failed to push to exception handling');
    } finally {
      setExceptionPushLoading(prev => ({ ...prev, [recordId]: false }));
    }
  };

  const formatAmount = (amount, currency = '') => {
    const value = parseFloat(amount || 0);
    // JPY doesn't have decimal places
    if (currency && currency.toUpperCase() === 'JPY') {
      return value.toFixed(0);
    }
    return value.toFixed(3);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return moment(dateString + ' 00:00:00').format('DD/MM/YYYY HH:mm:ss');
    }
    return moment.utc(dateString).utcOffset(8).format('DD/MM/YYYY HH:mm:ss');
  };

  const formatAcquirer = (acquirer) => {
    if (!acquirer || acquirer === 'N/A') return 'N/A';
    return acquirer.charAt(0).toUpperCase() + acquirer.slice(1).toLowerCase();
  };

  const renderTable = (data, count, currentPage, showExceptionButton = false) => {
    // If no data at all, show "Not found" without table structure
    if (count === 0) {
      return (
        <Card sx={{ borderRadius: 2, border: '1px solid #f0f0f0', position: 'relative' }}>
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography
              gutterBottom
              align="center"
              variant="subtitle1"
            >
              Not found
            </Typography>
          </Box>
        </Card>
      );
    }
    
    return (
      <Card sx={{ borderRadius: 2, border: '1px solid #f0f0f0', position: 'relative' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1500 }}>
            <TableHead>
              <TableRow>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Transaction ID</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Transaction Time</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Transaction Type</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Transaction State</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Terminal ID</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Merchant ID</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Merchant Name</Typography></TableCell>
                <TableCell align="right"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Transaction Amount</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Currency</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Brand</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Host</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Acquirer</Typography></TableCell>
                {showExceptionButton && (
                  <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Actions</Typography></TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item) => (
                <TableRow 
                  key={item.id} 
                  hover
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {item.transaction_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(item.transaction_time)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.transaction_type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.transaction_state}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.terminal_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.merchant_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, wordBreak: 'break-word' }}>
                      {item.merchant_name}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: item.transaction_amount < 0 ? '#f44336' : '#4caf50',
                        fontWeight: 500
                      }}
                    >
                      {formatAmount(item.transaction_amount, item.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.currency}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.brand}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.host}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatAcquirer(item.acquirer)}</Typography>
                  </TableCell>
                  {showExceptionButton && (
                    <TableCell>
                      <ButtonCustom
                        sx={{
                          ...styles.customButton,
                          minWidth: 0,
                          px: 2,
                          py: 0.5,
                          fontSize: '12px',
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                        }}
                        variant="contained"
                        title={exceptionPushLoading[item.id] ? 'Push to Exception Handling...' : 'Push to Exception Handling'}
                        disabled={exceptionPushLoading[item.id]}
                        onClick={() => handleOpenExceptionDialog(item)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <HeaderHelpPage title="Pending" isShowLink={false} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <ButtonCustom
          sx={{ ...styles.customButton, py: 0.75 }}
          variant="contained"
          title={exportLoading ? 'Exporting...' : 'Export to Excel'}
          icon={exportLoading ? <CircularProgress size={18} color="inherit" /> : null}
          onClick={handleExport}
          disabled={exportLoading}
        />
      </Box>
      <Card sx={{ mb: 2, px: 2, py: 2, borderRadius: 2, border: '1px solid #f0f0f0' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Currency
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  displayEmpty
                  sx={{
                    height: "30px",
                    "& .MuiSelect-select": {
                      fontSize: "14px",
                    },
                  }}
                >
                  {CURRENCY_OPTIONS.map((curr) => (
                    <MenuItem key={curr} value={curr}>
                      {curr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Settlement Cycle
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={settlementCycle}
                  onChange={(e) => setSettlementCycle(e.target.value)}
                  displayEmpty
                  sx={{
                    height: "30px",
                    "& .MuiSelect-select": {
                      fontSize: "14px",
                    },
                  }}
                >
                  <MenuItem value="">
                    <Box>All</Box>
                  </MenuItem>
                  {settlementCycleOptions.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Start Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  disableFuture
                  minDate={moment(INCOMING_PENDING_FILTER_START_DATE)}
                  inputFormat="YYYY-MM-DD"
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiFormLabel-root': {
                          lineHeight: '14px',
                          fontSize: '14px',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                          height: '14px',
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                End Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  disableFuture
                  minDate={startDate || moment(INCOMING_PENDING_FILTER_START_DATE)}
                  inputFormat="YYYY-MM-DD"
                  value={endDate}
                  onChange={(newValue) => {
                    setEndDate(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiFormLabel-root': {
                          lineHeight: '14px',
                          fontSize: '14px',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                          height: '14px',
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Transaction ID
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    height: '13px',
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Merchant ID
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value)}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    height: '13px',
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Merchant Name
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={merchantName}
                onChange={(e) => setMerchantName(e.target.value)}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    height: '13px',
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Terminal ID
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={terminalId}
                onChange={(e) => setTerminalId(e.target.value)}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    height: '13px',
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end">
          <ButtonCustom
            sx={{ ml: 1, mt: 2, ...styles.customButton }}
            icon={<Search />}
            variant="contained"
            title="Search"
            onClick={handleSearch}
          />
          <ButtonCustom
            sx={{ ml: 1, mt: 2 }}
            icon={<Refresh />}
            title="Reset"
            onClick={handleReset}
          />
        </Stack>
      </Card>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TabsMenu value={selectedTab} onChange={handleTabChange}>
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={`${PENDING_TYPES.NO_FUND.label} - ${PENDING_TYPES.NO_FUND.description}`} placement="top">
                    <Info sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                  </Tooltip>
                  <Chip 
                    label={noFundCount} 
                    size="small" 
                    color="warning" 
                    sx={{
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '24px',
                      '& .MuiChip-label': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        padding: '0 8px'
                      }
                    }}
                  />
                  {PENDING_TYPES.NO_FUND.label}
                </Box>
              } 
              value={PENDING_TYPES.NO_FUND.label} 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={`${PENDING_TYPES.NOT_SETTLED.label} - ${PENDING_TYPES.NOT_SETTLED.description}`} placement="top">
                    <Info sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                  </Tooltip>
                  <Chip 
                    label={notSettledCount} 
                    size="small" 
                    color="warning" 
                    sx={{
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '24px',
                      '& .MuiChip-label': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        padding: '0 8px'
                      }
                    }}
                  />
                  {PENDING_TYPES.NOT_SETTLED.label}
                </Box>
              } 
              value={PENDING_TYPES.NOT_SETTLED.label} 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={`${PENDING_TYPES.NO_PAYOUT.label} - ${PENDING_TYPES.NO_PAYOUT.description}`} placement="top">
                    <Info sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                  </Tooltip>
                  <Chip 
                    label={noPayoutCount} 
                    size="small" 
                    color="warning" 
                    sx={{
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '24px',
                      '& .MuiChip-label': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        padding: '0 8px'
                      }
                    }}
                  />
                  {PENDING_TYPES.NO_PAYOUT.label}
                </Box>
              } 
              value={PENDING_TYPES.NO_PAYOUT.label} 
            />
            
            <TabPanel value={PENDING_TYPES.NO_FUND.label}>
              <Box sx={{ mt: 2 }}>
                {loading ? (
                  <CircularLoader />
                ) : (
                  renderTable(noFundData, noFundCount, noFundPage, exceptionHandlingRole.includes("Add"))
                )}
              </Box>
            </TabPanel>
            
            <TabPanel value={PENDING_TYPES.NOT_SETTLED.label}>
              <Box sx={{ mt: 2 }}>
                {loading ? (
                  <CircularLoader />
                ) : (
                  renderTable(notSettledData, notSettledCount, notSettledPage, false)
                )}
              </Box>
            </TabPanel>
            
            <TabPanel value={PENDING_TYPES.NO_PAYOUT.label}>
              <Box sx={{ mt: 2 }}>
                {loading ? (
                  <CircularLoader />
                ) : (
                  renderTable(noPayoutData, noPayoutCount, noPayoutPage, false)
                )}
              </Box>
            </TabPanel>
          </TabsMenu>
        </Grid>
      </Grid>
      <ExceptionHandlingDialog
        open={exceptionDialogOpen}
        onClose={handleCloseExceptionDialog}
        onSubmit={handleConfirmException}
        loading={selectedExceptionRow ? exceptionPushLoading[selectedExceptionRow.id] : false}
        remarks={exceptionRemarks}
        setRemarks={setExceptionRemarks}
        action={exceptionAction}
        setAction={setExceptionAction}
      />
    </Container>
  );
};

export default Pending;
